import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { CustomButton } from "../../components/CustomButton";
import { useLayout } from "../../context/LayoutContext";
import {
  API_ADD_AI_CREDIT,
  API_DELETE_AI_AGENT,
  API_GET_AI_AGENTS,
  API_GET_AI_CREDIT_SUMMARY,
} from "../../api/company.service";
import "./company.css";

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const toCount = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const CUSTOM_CREDIT_PACK_ID = "credit-pack-custom";

const resolveAiDetails = (rawData, company, companyId) => {
  const fallbackCompanyId = toCount(company?.id || companyId, 0);
  const rawDetails =
    rawData?.data ||
    rawData ||
    company?.aiDetails ||
    company?.aiControlPanel ||
    company?.aiControl ||
    {};
  const details = rawDetails?.data || rawDetails || {};
  const creditSource =
    details?.creditDetails || rawDetails?.creditDetails || {};
  const totalCredit = toCount(creditSource?.credit, 0);
  const usedCredit = toCount(creditSource?.usedCredit, 0);
  const remainingCredit = toCount(
    creditSource?.remainingCredit,
    Math.max(totalCredit - usedCredit, 0),
  );

  return {
    companyId: toCount(details?.companyId, fallbackCompanyId),
    agentCount: toCount(details?.agentCount, 0),
    activeAgentCount: toCount(details?.activeAgentCount, 0),
    creditDetails: {
      companyId: toCount(creditSource?.companyId, fallbackCompanyId),
      credit: totalCredit,
      usedCredit,
      remainingCredit,
      inboundCount: toCount(creditSource?.inboundCount, 0),
      outboundCount: toCount(creditSource?.outboundCount, 0),
      createdAt: creditSource?.createdAt || "",
      updatedAt: creditSource?.updatedAt || "",
    },
  };
};

const resolveAiAgents = (rawData) => {
  const source = rawData?.data || rawData?.agents || rawData || [];

  if (!Array.isArray(source)) {
    return [];
  }

  return source.map((item, index) => {
    const agent = item?.agent || item || {};
    const knowledge = Array.isArray(item?.knowledge) ? item.knowledge : [];
    const agentId = agent?.id ?? null;

    return {
      id: agentId ?? `ai-agent-${index}`,
      agentId,
      agentName: agent?.agentName || "Untitled agent",
      providerName: agent?.providerName || "-",
      llmModel: agent?.llmModel || agent?.llmModal || "-",
      isActive: Boolean(agent?.isActive),
      companyId: toCount(agent?.companyId, 0),
      createdAt: agent?.createdAt || "",
      updatedAt: agent?.updatedAt || "",
      knowledgeCount: knowledge.length,
    };
  });
};

export const AIControlPanel = ({ company, companyId }) => {
  const { toastMessage } = useLayout();
  const creditPacks = useMemo(
    () => [
      { id: "credit-pack-500", amount: 500, credits: 2000, apiCredit: 2000 },
      { id: "credit-pack-1000", amount: 1000, credits: 5000, apiCredit: 5000 },
      {
        id: "credit-pack-5000",
        amount: 5000,
        credits: 30000,
        apiCredit: 30000,
        featured: true,
      },
    ],
    [],
  );
  const [creditDialogVisible, setCreditDialogVisible] = useState(false);
  const [creditStep, setCreditStep] = useState(1);
  const [selectedPackId, setSelectedPackId] = useState("");
  const [savedPackId, setSavedPackId] = useState("");
  const [customCreditValue, setCustomCreditValue] = useState(null);
  const [savedCustomCreditValue, setSavedCustomCreditValue] = useState(null);
  const [aiDetails, setAiDetails] = useState(() =>
    resolveAiDetails(null, company, companyId),
  );
  const [loadingAiDetails, setLoadingAiDetails] = useState(false);
  const [aiAgents, setAiAgents] = useState([]);
  const [loadingAiAgents, setLoadingAiAgents] = useState(false);
  const [savingCredit, setSavingCredit] = useState(false);
  const [deletingAgentId, setDeletingAgentId] = useState(null);
  const [agentPendingDelete, setAgentPendingDelete] = useState(null);

  useEffect(() => {
    setAiDetails((prev) => resolveAiDetails(prev, company, companyId));
  }, [company, companyId]);

  const loadAiDetails = useCallback(
    async ({ notifyOnError = true } = {}) => {
      if (!companyId) {
        setAiDetails(resolveAiDetails(null, company, companyId));
        return null;
      }

      setLoadingAiDetails(true);
      try {
        const response = await API_GET_AI_CREDIT_SUMMARY(companyId);
        setAiDetails(resolveAiDetails(response, company, companyId));
        return response;
      } catch (error) {
        if (notifyOnError) {
          toastMessage({
            severity: "error",
            detail:
              error?.response?.data?.message ||
              error?.message ||
              "Unable to load AI credit details.",
          });
        }
        return null;
      } finally {
        setLoadingAiDetails(false);
      }
    },
    [company, companyId, toastMessage],
  );

  const loadAiAgents = useCallback(
    async ({ notifyOnError = true } = {}) => {
      if (!companyId) {
        setAiAgents([]);
        return null;
      }

      setLoadingAiAgents(true);
      try {
        const response = await API_GET_AI_AGENTS(companyId);
        setAiAgents(resolveAiAgents(response));
        return response;
      } catch (error) {
        if (notifyOnError) {
          toastMessage({
            severity: "error",
            detail:
              error?.response?.data?.message ||
              error?.message ||
              "Unable to load AI agents.",
          });
        }
        return null;
      } finally {
        setLoadingAiAgents(false);
      }
    },
    [companyId, toastMessage],
  );

  useEffect(() => {
    loadAiDetails();
    loadAiAgents();
  }, [loadAiDetails, loadAiAgents]);

  const creditUsage = useMemo(() => {
    const total = Math.max(aiDetails?.creditDetails?.credit || 0, 1);
    const used = Math.min(aiDetails?.creditDetails?.usedCredit || 0, total);
    return Math.round((used / total) * 100);
  }, [aiDetails]);

  const aiAgentMetrics = useMemo(() => {
    return aiAgents.reduce(
      (acc, agent) => {
        acc.total += 1;
        if (agent.isActive) acc.active += 1;
        else acc.inactive += 1;
        return acc;
      },
      { total: 0, active: 0, inactive: 0 },
    );
  }, [aiAgents]);

  const displayedAgentTotal =
    loadingAiAgents && aiAgents.length === 0
      ? aiDetails.agentCount
      : aiAgentMetrics.total;
  const displayedActiveAgents =
    loadingAiAgents && aiAgents.length === 0
      ? aiDetails.activeAgentCount
      : aiAgentMetrics.active;
  const displayedInactiveAgents = Math.max(
    displayedAgentTotal - displayedActiveAgents,
    0,
  );

  const aiSummaryCards = useMemo(
    () => [
      {
        label: "Total Credit",
        value: aiDetails.creditDetails.credit,
        meta: "AI profile mapped",
      },
      {
        label: "Remaining Credit",
        value: aiDetails.creditDetails.remainingCredit,
        meta: "Available for automation",
      },
      {
        label: "Used Credit",
        value: aiDetails.creditDetails.usedCredit,
        meta: "Consumed automation credits",
      },
      {
        label: "Configured Agents",
        value: displayedAgentTotal,
        meta: `${displayedActiveAgents} active / ${displayedInactiveAgents} inactive`,
      },
    ],
    [
      aiDetails,
      displayedActiveAgents,
      displayedAgentTotal,
      displayedInactiveAgents,
    ],
  );

  const selectedPack = useMemo(
    () => creditPacks.find((pack) => pack.id === selectedPackId) || null,
    [creditPacks, selectedPackId],
  );

  const isCustomCreditSelected = selectedPackId === CUSTOM_CREDIT_PACK_ID;

  const customCreditAmount = useMemo(() => {
    const parsed = Math.floor(Number(customCreditValue));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [customCreditValue]);

  const selectedCreditRequest = useMemo(() => {
    if (selectedPack) {
      return { ...selectedPack, type: "pack" };
    }

    if (isCustomCreditSelected && customCreditAmount > 0) {
      return {
        id: CUSTOM_CREDIT_PACK_ID,
        amount: null,
        credits: customCreditAmount,
        apiCredit: customCreditAmount,
        type: "custom",
      };
    }

    return null;
  }, [selectedPack, isCustomCreditSelected, customCreditAmount]);

  const projectedCreditBalance = useMemo(() => {
    if (!selectedCreditRequest) return aiDetails.creditDetails.remainingCredit;
    return aiDetails.creditDetails.remainingCredit + selectedCreditRequest.credits;
  }, [aiDetails, selectedCreditRequest]);

  const openCreditDialog = () => {
    setCreditDialogVisible(true);
    setCreditStep(1);
    setSelectedPackId(savedPackId || "");
    setCustomCreditValue(
      savedPackId === CUSTOM_CREDIT_PACK_ID ? savedCustomCreditValue : null,
    );
  };

  const closeCreditDialog = () => {
    setCreditDialogVisible(false);
    setCreditStep(1);
    setSelectedPackId(savedPackId || "");
    setCustomCreditValue(
      savedPackId === CUSTOM_CREDIT_PACK_ID ? savedCustomCreditValue : null,
    );
  };

  const goToConfirmation = () => {
    if (!selectedCreditRequest) return;
    setCreditStep(2);
  };

  const refreshAiPanels = () => {
    loadAiDetails();
    loadAiAgents();
  };

  const saveCreditPlan = () => {
    if (!selectedCreditRequest || savingCredit) return;

    const run = async () => {
      try {
        setSavingCredit(true);
        await API_ADD_AI_CREDIT(companyId, {
          credit: selectedCreditRequest.apiCredit,
        });
        await loadAiDetails({ notifyOnError: false });
        setSavedPackId(selectedCreditRequest.id);
        setSavedCustomCreditValue(
          selectedCreditRequest.type === "custom"
            ? selectedCreditRequest.credits
            : null,
        );
        setCreditDialogVisible(false);
        setCreditStep(1);
        toastMessage({
          severity: "success",
          detail: "AI credit added successfully.",
        });
      } catch (error) {
        toastMessage({
          severity: "error",
          detail:
            error?.response?.data?.message ||
            error?.message ||
            "Unable to add AI credit.",
        });
      } finally {
        setSavingCredit(false);
      }
    };

    run();
  };

  const openDeleteDialog = (agent) => {
    setAgentPendingDelete(agent);
  };

  const closeDeleteDialog = () => {
    if (deletingAgentId) return;
    setAgentPendingDelete(null);
  };

  const handleDeleteAgent = async () => {
    if (!agentPendingDelete?.agentId || deletingAgentId) return;

    const currentAgent = agentPendingDelete;

    try {
      setDeletingAgentId(currentAgent.agentId);
      const response = await API_DELETE_AI_AGENT(
        companyId,
        currentAgent.agentId,
      );
      await Promise.all([
        loadAiAgents({ notifyOnError: false }),
        loadAiDetails({ notifyOnError: false }),
      ]);
      setAgentPendingDelete(null);
      toastMessage({
        severity: "success",
        detail:
          response?.message ||
          `AI agent "${currentAgent.agentName}" deleted successfully.`,
      });
    } catch (error) {
      toastMessage({
        severity: "error",
        detail:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to delete AI agent.",
      });
    } finally {
      setDeletingAgentId(null);
    }
  };

  const agentNameBody = (rowData) => {
    return (
      <div className="company-ai-agent-name">
        <strong>{rowData.agentName}</strong>
        <small>Agent ID #{rowData.agentId ?? "-"}</small>
      </div>
    );
  };

  const statusBody = (rowData) => {
    return (
      <span className={`status-pill ${rowData.isActive ? "success" : "warning"}`}>
        <i className={`pi ${rowData.isActive ? "pi-check-circle" : "pi-pause-circle"}`} />
        {rowData.isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  const knowledgeBody = (rowData) => {
    return <span className="company-ai-agent-badge">{rowData.knowledgeCount}</span>;
  };

  const updatedAtBody = (rowData) => {
    return formatDateTime(rowData.updatedAt || rowData.createdAt);
  };

  const actionBody = (rowData) => {
    const isDeletingCurrentAgent = deletingAgentId === rowData.agentId;

    return (
      <div className="company-ai-actions">
        <CustomButton
          label={isDeletingCurrentAgent ? "Deleting..." : "Delete"}
          icon="pi pi-trash"
          varient="outline"
          onClick={() => openDeleteDialog(rowData)}
          disabled={!rowData.agentId || Boolean(deletingAgentId)}
        />
      </div>
    );
  };

  return (
    <div className="company-tab-panel">
      <div className="surface-panel">
        <div className="surface-panel-header">
          <div>
            <h2 className="surface-panel-title">AI control panel</h2>
          </div>
        </div>

        <div className="company-ai-overview-grid">
          {aiSummaryCards.map((item) => (
            <div className="company-ai-summary-card" key={item.label}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
              <small>{item.meta}</small>
            </div>
          ))}
        </div>

        <div className="company-ai-layout">
          <div className="company-ai-credit-card">
            <div className="company-ai-block-header">
              <div>
                <h3>Credit health</h3>
                <p>Usage and remaining automation balance</p>
              </div>
              <div className="status-pill info">
                <i className="pi pi-bolt" />
                AI Ready
              </div>
            </div>

            <div className="company-ai-credit-meter">
              <div className="company-ai-credit-topline">
                <span>Total {aiDetails.creditDetails.credit}</span>
                <strong>
                  {aiDetails.creditDetails.usedCredit} used /{" "}
                  {aiDetails.creditDetails.remainingCredit} left
                </strong>
              </div>
              <div className="company-ai-progress-track" aria-hidden="true">
                <span style={{ width: `${creditUsage}%` }} />
              </div>
              <div className="company-ai-credit-footline">
                <span>
                  {loadingAiDetails ? "Syncing..." : `${creditUsage}% consumed`}
                </span>
                <span>
                  Updated {formatDateTime(aiDetails.creditDetails.updatedAt)}
                </span>
              </div>
            </div>
          </div>

          <div className="company-ai-side-grid">
            <div className="company-ai-traffic-card">
              <div className="company-ai-traffic-icon">
                <i className="pi pi-arrow-down-left" />
              </div>
              <div>
                <span>Inbound Messages</span>
                <strong>{aiDetails.creditDetails.inboundCount}</strong>
              </div>
            </div>
            <div className="company-ai-traffic-card">
              <div className="company-ai-traffic-icon">
                <i className="pi pi-arrow-up-right" />
              </div>
              <div>
                <span>Outbound Messages</span>
                <strong>{aiDetails.creditDetails.outboundCount}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="surface-panel">
        <div className="surface-panel-header">
          <div>
            <h2 className="surface-panel-title">AI agents</h2>
            <p className="surface-panel-subtitle">
              View all configured agents for this company and remove outdated ones.
            </p>
          </div>
          <div className="company-ai-table-tools">
            <span className="status-pill info">
              <i className="pi pi-bolt" />
              {displayedActiveAgents} active
            </span>
            <CustomButton
              label={loadingAiAgents || loadingAiDetails ? "Refreshing..." : "Refresh"}
              icon="pi pi-refresh"
              varient="outline"
              onClick={refreshAiPanels}
              disabled={loadingAiAgents || loadingAiDetails || Boolean(deletingAgentId)}
            />
          </div>
        </div>

        <div className="company-table-wrap">
          <DataTable
            value={aiAgents}
            dataKey="id"
            paginator
            rows={6}
            rowsPerPageOptions={[6, 12, 24]}
            responsiveLayout="scroll"
            loading={loadingAiAgents}
            emptyMessage="No AI agents found for this company yet."
          >
            <Column field="agentId" header="ID" />
            <Column header="Agent" body={agentNameBody} />
            <Column field="providerName" header="Provider" />
            <Column field="llmModel" header="Model" />
            <Column header="Knowledge" body={knowledgeBody} />
            <Column header="Status" body={statusBody} />
            <Column header="Updated At" body={updatedAtBody} />
            <Column header="Actions" body={actionBody} />
          </DataTable>
        </div>
      </div>

      <div className="surface-panel">
        <div className="surface-panel-header">
          <div>
            <h2 className="surface-panel-title">Add AI credit</h2>
          </div>
        </div>

        <div className="company-ai-add-card">
          <div className="company-ai-add-copy">
            <h3>Top up AI automation credits</h3>
            <p>
              Select a fixed pack or enter a custom credit amount, then review
              and save the top up request.
            </p>
          </div>

          <div className="company-ai-add-actions">
            <CustomButton
              label="Add AI Credit"
              icon="pi pi-plus"
              onClick={openCreditDialog}
              disabled={loadingAiDetails}
            />
          </div>
        </div>
      </div>

      <Dialog
        header="Top up AI credits"
        visible={creditDialogVisible}
        onHide={closeCreditDialog}
        style={{ width: "min(760px, 94vw)" }}
        className="company-ai-dialog"
        draggable={false}
        resizable={false}
      >
        <div className="company-ai-dialog-body">
          <div className="company-ai-stepper">
            <div
              className={`company-ai-step ${creditStep === 1 ? "active" : "complete"}`}
            >
              <span className="company-ai-step-index">1</span>
              <div>
                <strong>Select Top Up</strong>
                <small>Choose a pack or custom credit</small>
              </div>
            </div>
            <div
              className={`company-ai-step ${creditStep === 2 ? "active" : ""}`}
            >
              <span className="company-ai-step-index">2</span>
              <div>
                <strong>Confirmation</strong>
                <small>Review selected plan details</small>
              </div>
            </div>
          </div>

          {creditStep === 1 ? (
            <div className="company-ai-selection-grid">
              {creditPacks.map((pack) => (
                <button
                  type="button"
                  key={pack.id}
                  className={`company-ai-pack-card selectable ${
                    pack.featured ? "featured" : ""
                  } ${selectedPackId === pack.id ? "selected" : ""}`}
                  onClick={() => setSelectedPackId(pack.id)}
                >
                  <div className="company-ai-pack-top">
                    <span className="company-ai-pack-amount">
                      INR {pack.amount}
                    </span>
                    {pack.featured ? (
                      <div className="status-pill info">
                        <i className="pi pi-star-fill" />
                        Best value
                      </div>
                    ) : null}
                  </div>

                  <strong>{pack.credits.toLocaleString()} credits</strong>
                  <small>
                    {Math.round(pack.credits / pack.amount).toLocaleString()}{" "}
                    credits / INR
                  </small>
                </button>
              ))}

              <div
                className={`company-ai-pack-card company-ai-custom-card ${
                  isCustomCreditSelected ? "selected" : ""
                }`}
                onClick={() => setSelectedPackId(CUSTOM_CREDIT_PACK_ID)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedPackId(CUSTOM_CREDIT_PACK_ID);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className="company-ai-pack-top">
                  <span className="company-ai-pack-amount">Custom Credit</span>
                  <div className="status-pill info">
                    <i className="pi pi-pencil" />
                    Manual entry
                  </div>
                </div>

                <strong>Add exact credits</strong>
                <small>
                  Enter the exact number of AI credits that should be added.
                </small>

                <div className="company-ai-custom-input">
                  <label htmlFor="custom-ai-credit">Credits to add</label>
                  <InputNumber
                    id="custom-ai-credit"
                    value={customCreditValue}
                    onChange={(event) => {
                      setSelectedPackId(CUSTOM_CREDIT_PACK_ID);
                      setCustomCreditValue(event.value);
                    }}
                    onFocus={() => setSelectedPackId(CUSTOM_CREDIT_PACK_ID)}
                    useGrouping={false}
                    min={1}
                    maxFractionDigits={0}
                    placeholder="Enter credit amount"
                  />
                </div>

                {isCustomCreditSelected ? (
                  customCreditAmount > 0 ? (
                    <p className="company-ai-custom-note">
                      Projected remaining balance:{" "}
                      {projectedCreditBalance.toLocaleString()}
                    </p>
                  ) : (
                    <p className="company-ai-custom-error">
                      Enter at least 1 credit to continue.
                    </p>
                  )
                ) : null}
              </div>
            </div>
          ) : (
            <div className="company-ai-confirm-layout">
              <div className="company-ai-confirm-card">
                <div className="company-ai-block-header">
                  <div>
                    <h3>Selected plan</h3>
                    <p>
                      Review the complete credit purchase details before saving.
                    </p>
                  </div>
                  <div className="status-pill info">
                    <i className="pi pi-check-circle" />
                    Ready to save
                  </div>
                </div>

                <div className="company-ai-confirm-grid">
                  <div className="company-ai-confirm-item">
                    <label>Top Up Type</label>
                    <p>
                      {selectedCreditRequest?.type === "custom"
                        ? "Custom credit"
                        : "Fixed pack"}
                    </p>
                  </div>
                  <div className="company-ai-confirm-item">
                    <label>Credits Added</label>
                    <p>
                      {selectedCreditRequest?.credits?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="company-ai-confirm-item">
                    <label>
                      {selectedCreditRequest?.type === "custom"
                        ? "Entry Mode"
                        : "Plan Price"}
                    </label>
                    <p>
                      {selectedCreditRequest?.type === "custom"
                        ? "Manual credit adjustment"
                        : `INR ${selectedCreditRequest?.amount || 0}`}
                    </p>
                  </div>
                  <div className="company-ai-confirm-item">
                    <label>Credit Ratio</label>
                    <p>
                      {selectedCreditRequest?.type === "custom"
                        ? "Direct credit entry"
                        : `${Math.round(
                            selectedCreditRequest?.credits /
                              selectedCreditRequest?.amount,
                          ).toLocaleString()} credits / INR`}
                    </p>
                  </div>
                  <div className="company-ai-confirm-item">
                    <label>Current Remaining</label>
                    <p>
                      {aiDetails.creditDetails.remainingCredit.toLocaleString()}
                    </p>
                  </div>
                  <div className="company-ai-confirm-item">
                    <label>Projected Remaining</label>
                    <p>{projectedCreditBalance.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="company-ai-dialog-footer">
            {creditStep === 2 ? (
              <CustomButton
                label="Back"
                icon="pi pi-arrow-left"
                onClick={() => setCreditStep(1)}
                varient="outline"
              />
            ) : (
              <span />
            )}

            <div className="company-ai-dialog-actions">
              <CustomButton
                label="Cancel"
                onClick={closeCreditDialog}
                varient="outline"
                disabled={savingCredit}
              />
              {creditStep === 1 ? (
                <CustomButton
                  label="Continue"
                  icon="pi pi-arrow-right"
                  onClick={goToConfirmation}
                  disabled={!selectedCreditRequest || savingCredit}
                />
              ) : (
                <CustomButton
                  label={savingCredit ? "Saving..." : "Save"}
                  icon="pi pi-check"
                  onClick={saveCreditPlan}
                  disabled={!selectedCreditRequest || savingCredit}
                />
              )}
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Delete AI agent"
        visible={Boolean(agentPendingDelete)}
        onHide={closeDeleteDialog}
        style={{ width: "min(560px, 92vw)" }}
        draggable={false}
        resizable={false}
      >
        <p className="company-ai-delete-copy">
          This will permanently remove{" "}
          <strong>{agentPendingDelete?.agentName || "this AI agent"}</strong> from
          company #{companyId}. This action cannot be undone.
        </p>

        <div className="company-ai-confirm-card">
          <div className="company-ai-confirm-grid">
            <div className="company-ai-confirm-item">
              <label>Agent</label>
              <p>{agentPendingDelete?.agentName || "-"}</p>
            </div>
            <div className="company-ai-confirm-item">
              <label>Provider</label>
              <p>{agentPendingDelete?.providerName || "-"}</p>
            </div>
            <div className="company-ai-confirm-item">
              <label>Model</label>
              <p>{agentPendingDelete?.llmModel || "-"}</p>
            </div>
            <div className="company-ai-confirm-item">
              <label>Status</label>
              <p>{agentPendingDelete?.isActive ? "Active" : "Inactive"}</p>
            </div>
          </div>
        </div>

        <div className="company-ai-dialog-actions">
          <CustomButton
            label="Cancel"
            onClick={closeDeleteDialog}
            varient="outline"
            disabled={Boolean(deletingAgentId)}
          />
          <CustomButton
            label={deletingAgentId ? "Deleting..." : "Delete Agent"}
            icon="pi pi-trash"
            onClick={handleDeleteAgent}
            disabled={!agentPendingDelete?.agentId || Boolean(deletingAgentId)}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default AIControlPanel;
